import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { applicationApi, studentApi } from '@/api';
import { resolveFileUrl } from '@/api/client';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  LoadingState,
  Select,
  Textarea,
  useToast,
} from '@/components/ui';
import { RecruitmentProgress } from '@/features/applications/RecruitmentProgress';
import { APPLICATION_TONES, STUDENT_APPLICATION_LABELS } from '@/features/applications/status';
import { StudentApplicationRounds } from '@/features/interviews/StudentApplicationRounds';
import { ApplicationThread } from '@/features/messages/ApplicationThread';
import { AnswerOfferActions } from '@/features/offers/AnswerOfferActions';
import { historyTitle } from '@/features/applications/history';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';

/** How long is left to edit, in words. Null once there is nothing to count. */
/**
 * What the candidate can expect next, in their words. Every open status has
 * one, so "where do I stand?" never needs a guess. `link` is where to act.
 */
const WHAT_NEXT = {
  SUBMITTED: {
    text: 'The company has your application. You will hear from them here once they review it.',
  },
  UNDER_REVIEW: { text: 'The company is reading your application right now.' },
  SHORTLISTED: {
    text: 'You are through to the next stage. Expect an interview invitation, or even a direct offer.',
  },
  INTERVIEW: {
    text: 'You have an interview booked. Confirm the time so the company knows you will be there.',
    link: { to: paths.student.interviews, label: 'See your interview' },
  },
  DECISION_PENDING: {
    text: 'Your interview is done and the company is deciding: another round, an offer, or a decision either way.',
  },
  OFFERED: {
    text: 'You have an offer. Read the terms and accept or decline before the respond-by date.',
  },
  OFFER_ACCEPTED: {
    text: 'You accepted the offer. The company will mark you as joined on your first day.',
  },
  HIRED: { text: 'You joined. Congratulations, and good luck with the internship!' },
};

function timeLeft(deadline) {
  if (!deadline) return null;
  const minutes = Math.round((new Date(deadline).getTime() - Date.now()) / 60000);
  if (minutes <= 0) return null;
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

/**
 * One application, and everything the applicant is allowed to do to it.
 *
 * This is where a status stops being a badge and becomes an account of what
 * happened: the history the pipeline already recorded, read from the side that
 * has been waiting on it. It is also the target of every application
 * notification, which is why the route exists at this path and not another.
 */
export function StudentApplicationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ cover_letter: '', answers: '', resume_id: '' });
  const [confirmingWithdrawal, setConfirmingWithdrawal] = useState(false);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['student-application', id],
    queryFn: () => applicationApi.getApplication(id),
  });

  // Only fetched once the form is open: a CV list is of no interest to someone
  // reading the history of an application they can no longer change.
  const { data: resumes } = useQuery({
    queryKey: ['student-resumes'],
    queryFn: studentApi.listResumes,
    enabled: editing,
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      cover_letter: data.cover_letter ?? '',
      answers: data.answers ?? '',
      resume_id: data.resume?.id ?? '',
    });
  }, [data]);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['student-application', id] });
    void queryClient.invalidateQueries({ queryKey: ['student-applications'] });
    void queryClient.invalidateQueries({ queryKey: ['student-dashboard'] });
  }

  const save = useMutation({
    mutationFn: (input) => applicationApi.updateApplication(id, input),
    onSuccess: () => {
      toast.success('Application updated');
      invalidate();
      setEditing(false);
    },
    onError: (saveError) => {
      // The window can shut between the page loading and this request. The
      // server is the authority on that, so its answer is shown and the page
      // refetched rather than the button being re-enabled on a stale flag.
      toast.error(errorMessage(saveError, 'Could not update the application'));
      invalidate();
      setEditing(false);
    },
  });

  const withdraw = useMutation({
    mutationFn: (reason) => applicationApi.withdrawApplication(id, reason),
    onSuccess: () => {
      toast.success('Application withdrawn');
      invalidate();
      setConfirmingWithdrawal(false);
    },
    onError: (withdrawError) => {
      toast.error(errorMessage(withdrawError, 'Could not withdraw the application'));
      setConfirmingWithdrawal(false);
    },
  });

  if (isPending) return <LoadingState label="Loading your application…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const remaining = data.can_edit ? timeLeft(data.edit_deadline) : null;
  const history = data.history ?? [];

  return (
    <div>
      <Card className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 flex-1 basis-64 gap-4">
            <span
              aria-hidden="true"
              className="bg-brand-50 text-brand-700 ring-brand-100 grid size-12 shrink-0 place-items-center rounded-xl ring-1 ring-inset"
            >
              <Icon name="application" size={22} />
            </span>
            <div className="min-w-0">
              <h1 className="text-ink text-xl leading-snug font-semibold tracking-tight">
                {data.internship.title}
              </h1>
              <p className="text-ink-muted mt-0.5 text-sm">
                {data.internship.company_name}
                {data.internship.location ? ` · ${data.internship.location}` : ''}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <Badge tone={APPLICATION_TONES[data.status] ?? 'neutral'} dot>
                  {STUDENT_APPLICATION_LABELS[data.status] ?? data.status}
                </Badge>
                <span className="text-ink-muted inline-flex items-center gap-1.5 text-xs">
                  <Icon name="calendar" size={14} />
                  Applied {formatDate(data.submitted_at)}
                </span>
              </div>
            </div>
          </div>
          <Button variant="secondary" onClick={() => navigate(paths.student.applications)}>
            <Icon name="arrowRight" size={16} className="rotate-180" />
            All applications
          </Button>
        </div>
      </Card>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-6">
          {data.status === 'REJECTED' && data.rejection_reason && (
            <Alert tone="info" title="What they said">
              {data.rejection_reason}
            </Alert>
          )}

          <Card>
            <CardHeader title="Your progress" />
            <RecruitmentProgress
              status={data.status}
              history={history}
              labels={{ joined: 'Joined', decision: 'Decision' }}
            />
            {WHAT_NEXT[data.status] && (
              <div className="bg-brand-50/70 ring-brand-100 rounded-input mt-5 flex flex-wrap items-center justify-between gap-3 px-4 py-3 ring-1 ring-inset">
                <p className="text-ink flex min-w-0 flex-1 basis-56 items-start gap-2 text-sm">
                  <Icon name="info" size={16} className="text-brand-600 mt-0.5 shrink-0" />
                  {WHAT_NEXT[data.status].text}
                </p>
                {/* An open offer is answered right here rather than on another page. */}
                {data.status === 'OFFERED' && <AnswerOfferActions applicationId={id} />}
                {WHAT_NEXT[data.status].link && (
                  <LinkButton size="sm" to={WHAT_NEXT[data.status].link.to}>
                    {WHAT_NEXT[data.status].link.label}
                  </LinkButton>
                )}
              </div>
            )}
          </Card>

          <StudentApplicationRounds applicationId={id} />

          <Card>
            <CardHeader
              title="What you sent"
              description={`Submitted ${formatDate(data.submitted_at)}`}
              action={
                data.can_edit &&
                !editing && (
                  <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                    Edit
                  </Button>
                )
              }
            />

            {data.can_edit && !editing && (
              <Alert tone="info" className="mb-4">
                You can still change this application
                {remaining ? ` for another ${remaining}` : ''} — until the company opens it,
                whichever comes first.
              </Alert>
            )}

            {/* Why editing is closed, not merely that it is. The deadline
                survives its own expiry precisely so this can be said; once
                the company has engaged the server sends null, because then
                the clock was never the reason. */}
            {!data.can_edit && data.edit_deadline && (
              <p className="text-ink-muted mb-4 text-xs">
                Editing closed on {formatDate(data.edit_deadline)}.
              </p>
            )}
            {!data.can_edit && !data.edit_deadline && data.company_has_acted && (
              <p className="text-ink-muted mb-4 text-xs">
                {data.internship.company_name} has opened this application, so it can no longer be
                changed.
              </p>
            )}

            {editing ? (
              <div className="space-y-4">
                <Select
                  label="CV"
                  value={form.resume_id}
                  onChange={(event) => setForm({ ...form, resume_id: event.target.value })}
                  hint="The CV the company downloads when they open your application."
                >
                  <option value="">No CV attached</option>
                  {(resumes ?? []).map((resume) => (
                    <option key={resume.id} value={resume.id}>
                      {resume.file_name}
                    </option>
                  ))}
                </Select>
                <Textarea
                  label="Cover letter"
                  rows={8}
                  value={form.cover_letter}
                  onChange={(event) => setForm({ ...form, cover_letter: event.target.value })}
                />
                <Textarea
                  label="Answers"
                  rows={4}
                  value={form.answers}
                  onChange={(event) => setForm({ ...form, answers: event.target.value })}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    isLoading={save.isPending}
                    onClick={() =>
                      // Sent as null rather than "": the field is being
                      // cleared, and an empty string is not an empty field.
                      save.mutate({
                        cover_letter: form.cover_letter.trim() || null,
                        answers: form.answers.trim() || null,
                        resume_id: form.resume_id || null,
                      })
                    }
                  >
                    Save application
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-ink-muted text-xs font-medium uppercase">CV</p>
                  {data.resume ? (
                    <a
                      href={resolveFileUrl(data.resume.download_url)}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-brand-700 mt-1 inline-flex max-w-full items-center gap-1.5 text-sm hover:underline"
                    >
                      <Icon name="document" size={16} className="shrink-0" />
                      <span className="min-w-0 truncate">{data.resume.file_name}</span>
                    </a>
                  ) : (
                    <p className="text-ink-muted text-sm">No CV was attached.</p>
                  )}
                </div>
                <div>
                  <p className="text-ink-muted text-xs font-medium uppercase">Cover letter</p>
                  <p className="text-ink mt-1 text-sm whitespace-pre-wrap">
                    {data.cover_letter || '—'}
                  </p>
                </div>
                {data.answers && (
                  <div>
                    <p className="text-ink-muted text-xs font-medium uppercase">Answers</p>
                    <p className="text-ink mt-1 text-sm whitespace-pre-wrap">{data.answers}</p>
                  </div>
                )}
              </div>
            )}
          </Card>

          <ApplicationThread
            applicationId={data.id}
            title={`Messages with ${data.internship.company_name}`}
            description="Ask the recruiter about your application, or share documents."
            messagesPath={paths.student.messages}
          />

          <Card>
            <CardHeader
              title="Progress"
              description="Every move on your application, oldest first."
            />
            {history.length === 0 ? (
              <EmptyState
                compact
                icon={<Icon name="clockOutline" size={16} />}
                title="Nothing has happened yet."
              />
            ) : (
              <ol className="before:bg-line relative space-y-5 pl-6 before:absolute before:top-1 before:bottom-1 before:left-2 before:w-px">
                {history.map((entry, index) => {
                  // Oldest first, so the last entry is where it stands now.
                  const isCurrent = index === history.length - 1;
                  return (
                    <li key={`${entry.to_status}-${index}`} className="relative">
                      <span
                        aria-hidden="true"
                        className={cn(
                          'absolute top-1 -left-[21.5px] size-3 rounded-full ring-4',
                          isCurrent ? 'bg-brand-600 ring-brand-100' : 'bg-line ring-surface',
                        )}
                      />
                      <p
                        className={cn(
                          'text-sm font-medium',
                          isCurrent ? 'text-ink' : 'text-ink-muted',
                        )}
                      >
                        {historyTitle(
                          entry,
                          (status) => STUDENT_APPLICATION_LABELS[status] ?? status,
                        )}
                      </p>
                      <p className="text-ink-muted mt-0.5 text-xs">
                        {formatDate(entry.created_at)}
                      </p>
                      {entry.note && (
                        <p className="text-ink-muted bg-surface-muted/60 rounded-input mt-2 px-3 py-2 text-xs">
                          {entry.note}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Status" />
            <Badge tone={APPLICATION_TONES[data.status] ?? 'neutral'} dot>
              {STUDENT_APPLICATION_LABELS[data.status] ?? data.status}
            </Badge>
            <dl className="border-line mt-4 space-y-2 border-t pt-4 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-ink-muted">Applied</dt>
                <dd className="text-ink">{formatDate(data.submitted_at)}</dd>
              </div>
              {data.reviewed_at && (
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-muted">Opened</dt>
                  <dd className="text-ink">{formatDate(data.reviewed_at)}</dd>
                </div>
              )}
              {data.decided_at && (
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-muted">Closed</dt>
                  <dd className="text-ink">{formatDate(data.decided_at)}</dd>
                </div>
              )}
            </dl>
            <LinkButton
              to={paths.internshipDetail(data.internship.slug)}
              variant="outline"
              size="sm"
              className="mt-4 w-full"
            >
              <Icon name="internship" size={16} />
              View the listing
            </LinkButton>
          </Card>

          {data.can_withdraw && (
            <Card>
              <CardHeader
                title="Withdraw"
                description="Take yourself out of the running for this role."
              />
              <Button
                variant="secondary"
                size="sm"
                fullWidth
                onClick={() => setConfirmingWithdrawal(true)}
              >
                Withdraw application
              </Button>
              <p className="text-ink-muted mt-2 text-xs">
                Possible until the application deadline, and only while the company has not acted on
                it yet.
              </p>
            </Card>
          )}

          {/* Why the option is gone, rather than a button that silently
              disappeared. Declining an offer, or a message to the company, is
              the way out from here. */}
          {!data.can_withdraw && data.withdraw_blocked_reason && (
            <Card>
              <CardHeader title="Withdraw" />
              <p className="text-ink-muted text-sm">{data.withdraw_blocked_reason}</p>
            </Card>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmingWithdrawal}
        title="Withdraw application"
        message={
          // Only reachable before the company acts and before the deadline,
          // so the application is still entirely the student's to take back.
          'The company has not acted on this application yet. You can apply again later, while the listing is open.'
        }
        confirmLabel="Withdraw"
        isLoading={withdraw.isPending}
        onConfirm={() => withdraw.mutate(null)}
        onCancel={() => setConfirmingWithdrawal(false)}
      />
    </div>
  );
}
